import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getFloc } from '../../services/flocService';
import { getAssets } from '../../services/assetService';
import { commissionAsset } from '../../services/assetInstallationService';
import { Asset, FunctionalLocation } from '@easydone/shared';
import { CubeIcon, CheckIcon } from '@heroicons/react/24/outline';

export function CommissionToFlocPage() {
  const { flocId } = useParams<{ flocId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [floc, setFloc] = useState<FunctionalLocation | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [conditionAtInstall, setConditionAtInstall] = useState(100);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [commissioning, setCommissioning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [flocId, currentOrg]);

  const loadData = async () => {
    if (!flocId || !currentOrg) return;

    setLoading(true);
    setError('');

    try {
      const [flocData, allAssets] = await Promise.all([
        getFloc(currentOrg.id, flocId),
        getAssets(currentOrg.id),
      ]);

      if (!flocData) {
        setError('FLOC not found');
        return;
      }

      // Filter for assets in storage only
      const storageAssets = allAssets.filter(
        (asset) => asset.lifecycleStatus === 'in_storage'
      );

      setFloc(flocData);
      setAssets(storageAssets);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCommission = async () => {
    if (!selectedAsset || !floc || !currentOrg || !currentUser) return;

    setCommissioning(true);
    try {
      await commissionAsset(
        currentOrg.id,
        {
          assetId: selectedAsset.id,
          flocId: flocId!,
          conditionAtInstall,
          installationNotes: notes,
        },
        currentUser.uid,
        currentUser.email || 'Unknown User'
      );

      alert('Asset commissioned successfully!');
      navigate(`/flocs/${flocId}`);
    } catch (err: any) {
      console.error('Failed to commission asset:', err);
      alert('Failed to commission asset: ' + err.message);
    } finally {
      setCommissioning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !floc) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center text-red-600 py-8">{error || 'FLOC not found'}</div>
        <button onClick={() => navigate('/flocs')} className="btn btn-secondary">
          Back to FLOCs
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Commission Asset to FLOC</h1>
        <p className="text-gray-600 mt-1">
          FLOC: {floc.code} - {floc.name}
        </p>
      </div>

      {!selectedAsset ? (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <CubeIcon className="w-5 h-5 mr-2" />
            Select Asset from Storage
          </h2>

          {assets.length > 0 ? (
            <div className="space-y-3">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{asset.name}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Serial: {asset.serialNumber} • Type: {asset.type}
                      </p>
                      {asset.storageLocation && (
                        <p className="text-sm text-gray-500 mt-1">
                          Storage: {asset.storageLocation}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          Condition: {asset.conditionScore}/100
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          asset.conditionGrade === 'A' ? 'bg-green-100 text-green-700' :
                          asset.conditionGrade === 'B' ? 'bg-blue-100 text-blue-700' :
                          asset.conditionGrade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Grade {asset.conditionGrade}
                        </span>
                      </div>
                    </div>
                    <div className="text-primary-600 font-medium text-sm">
                      Select →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No assets available in storage</p>
              <button
                onClick={() => navigate('/assets')}
                className="btn btn-secondary text-sm"
              >
                View All Assets
              </button>
            </div>
          )}

          <div className="mt-6">
            <button onClick={() => navigate(`/flocs/${flocId}`)} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Selected Asset */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Selected Asset</h2>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900">{selectedAsset.name}</p>
              <p className="text-sm text-gray-600 mt-1">
                Serial: {selectedAsset.serialNumber} • Type: {selectedAsset.type}
              </p>
              <button
                onClick={() => setSelectedAsset(null)}
                className="text-sm text-primary-600 hover:text-primary-700 mt-2"
              >
                ← Change Asset
              </button>
            </div>
          </div>

          {/* Commission Form */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Installation Details</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition at Installation (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={conditionAtInstall}
                  onChange={(e) => setConditionAtInstall(parseInt(e.target.value) || 0)}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current condition score: {selectedAsset.conditionScore}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Installation Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="input w-full"
                  placeholder="Any notes about this installation..."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleCommission}
                disabled={commissioning}
                className="btn btn-primary flex items-center"
              >
                <CheckIcon className="w-5 h-5 mr-2" />
                {commissioning ? 'Commissioning...' : 'Commission Asset'}
              </button>
              <button
                onClick={() => navigate(`/flocs/${flocId}`)}
                disabled={commissioning}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
